import React from 'react'
import {
  AddCompanyGroupMembers_addCompanyGroupMembers,
  GroupMemberRole,
  AddCompanyGroupMembersUserInput,
  useAddCompanyGroupMembersMutation,
  UserRole,
  useGroupForm,
  AllUsers_users_users,
  useSubperson,
  Subpersons_subperson,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useToaster } from 'utils/useToaster'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { FindOrInviteUser } from 'sharedComponents/FindOrInviteUser'
import Button from 'components/Button'
import Card from 'components/Card'
import { Dialog } from 'components/Dialog'
import Datalist from 'components/Datalist'
import { SelectInput } from 'components/SelectInput'
import { Dropdown } from 'components/AutoCompleteDropdown'
import Icon from 'components/Icon/Icon'

interface AddNewGroupMembersProps {
  buttonLabel?: string
  companyId: string
  groupId: string
  canSetRole?: boolean
  isSearchingInCompanyOnly?: boolean
  onAddSuccess: (users: AddCompanyGroupMembers_addCompanyGroupMembers[]) => void
}

const AddNewGroupMembers: React.VFC<AddNewGroupMembersProps> = ({
  buttonLabel = 'Add members',
  groupId,
  companyId,
  canSetRole = true,
  isSearchingInCompanyOnly = true,
  onAddSuccess,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [isOpenNewUserDialog, setIsOpenNewUserDialog] = React.useState(false)
  const defaultUser = {
    id: '',
    firstName: null,
    lastName: null,
    email: null,
    parentId: null,
  }
  const [selectedUser, setSelectedUser] = React.useState<Omit<AllUsers_users_users, '__typename'>>(
    defaultUser
  )
  const { data: subperson, loading: isSubpersonLoading } = useSubperson({
    userId: selectedUser?.id,
    companyId,
    groupId,
  })

  const handleOpenNewUserDialog = React.useCallback(() => {
    setIsOpenNewUserDialog(true)
  }, [])

  const handleCloseNewUserDialog = React.useCallback(() => {
    setIsOpenNewUserDialog(false)
    setSelectedUser(defaultUser)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { setToastMessage } = useToaster()
  const [
    addCompanyGroupMembers,
    addCompanyGroupMembersMutation,
  ] = useAddCompanyGroupMembersMutation()

  const { groupForm, handleAddMember, handleDeleteMember, handleSelectMemberRole } = useGroupForm({
    validationSchema: 'membersOnly',
    onSubmit: async (values, formikHelpers) => {
      try {
        const { data } = await addCompanyGroupMembers({
          variables: {
            inputData: {
              companyId,
              groupId,
              Members: values.Members.map(({ User, role }) => ({
                role,
                User: {
                  email: User.email,
                  id: User.id,
                  parentId: User.parentId,
                  roles: [UserRole.PATIENT],
                },
              })) as AddCompanyGroupMembersUserInput[],
            },
          },
        })
        if (data) {
          formikHelpers.resetForm()
          handleCloseNewUserDialog()
          onAddSuccess(data?.addCompanyGroupMembers)
          setToastMessage('Members successfully added', 'success')
          setSelectedUser(defaultUser)
        }
      } catch {
        // ignore
      }
    },
  })

  const userFilter = React.useMemo(
    () =>
      isSearchingInCompanyOnly
        ? {
            Company: { id: companyId },
          }
        : undefined,
    [companyId, isSearchingInCompanyOnly]
  )

  React.useEffect(() => {
    if (
      subperson !== undefined &&
      !subperson?.subperson?.length &&
      selectedUser.id !== defaultUser.id
    ) {
      handleAddMember(selectedUser)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subperson])

  return (
    <>
      <Button onClick={handleOpenNewUserDialog} leftIcon={<Icon.Plus size="L" />}>
        {buttonLabel}
      </Button>
      <Dialog
        variant={isDesktop ? 'right' : 'fullscreen'}
        maxWidth={isDesktop ? '550px' : '100%'}
        isOpen={isOpenNewUserDialog}
        onDismiss={handleCloseNewUserDialog}
      >
        <Card.Title onDismiss={handleCloseNewUserDialog}>Add members</Card.Title>
        <Card.Content>
          <FindOrInviteUser filter={userFilter} onUserSelect={setSelectedUser} />
          {subperson?.subperson && subperson.subperson.length > 0 && (
            <Dropdown
              name="user"
              value=""
              isLoading={isSubpersonLoading}
              options={[
                {
                  value: selectedUser.id,
                  text: selectedUser.email,
                },
                ...subperson?.subperson?.map((e: Subpersons_subperson | null) => ({
                  value: e?.id || 'No value',
                  text: e?.firstName,
                })),
              ]}
              label="Select main user or subperson"
              isWithOptionsFilter={false}
              selectOnNavigation={false}
              onSelectChange={(_: React.SyntheticEvent<HTMLElement>, data: { value: string }) => {
                const user =
                  data.value === selectedUser.id
                    ? selectedUser
                    : subperson
                        .subperson!.filter(
                          (sub: Subpersons_subperson | null) => sub?.id === data.value
                        )
                        .map((sub: Subpersons_subperson | null) => ({
                          email: selectedUser.email,
                          firstName: sub?.firstName as string,
                          lastName: sub?.lastName as string,
                          id: sub?.id as string,
                          parentId: sub?.parentId as string,
                        }))[0]

                handleAddMember(user)
              }}
            />
          )}
          {groupForm.values.Members.length > 0 && (
            <Datalist
              header={<Datalist.Title>Members</Datalist.Title>}
              footer={
                <Button
                  onClick={() => {
                    groupForm.handleSubmit()
                  }}
                  isFetching={addCompanyGroupMembersMutation.loading}
                >
                  Add members
                </Button>
              }
              data={groupForm.values.Members}
              renderTableHeader={() => (
                <Datalist.Row display="grid" gridTemplateColumns="repeat(2, 1fr) 2.5rem">
                  <Datalist.HeaderCell label="Email" />
                  <Datalist.HeaderCell label="Role" />
                  <Datalist.HeaderCell label="Actions" />
                </Datalist.Row>
              )}
              shouldRenderTableHeader={isDesktop}
              renderTableRow={(member) => (
                <Datalist.Row display="grid" gridTemplateColumns="repeat(2, 1fr) 2.5rem">
                  <Datalist.Cell label="Email">
                    {!member.subperson
                      ? member.User.email
                      : `${member.User.firstName} (${member.User.email})`}
                  </Datalist.Cell>
                  <Datalist.Cell label="Role">
                    <SelectInput
                      value={member.role}
                      name="memberRole"
                      size="small"
                      onChange={(event) =>
                        handleSelectMemberRole(
                          event.target.value as GroupMemberRole,
                          member.User.id
                        )
                      }
                      disabled={!canSetRole || member.subperson}
                      options={[
                        {
                          label: 'Member',
                          value: GroupMemberRole.Member,
                        },
                        {
                          label: 'Manager',
                          value: GroupMemberRole.Manager,
                        },
                        {
                          label: 'Coordinator',
                          value: GroupMemberRole.Coordinator,
                        },
                      ]}
                      hasBottomContent={false}
                      hasTopContent={false}
                    />
                  </Datalist.Cell>
                  <Datalist.Cell label="Actions">
                    <Button
                      onClick={() => handleDeleteMember(member.User.id)}
                      appearance="ghost"
                      colorVariant="danger"
                      size="small"
                    >
                      <Icon.Trash />
                    </Button>
                  </Datalist.Cell>
                </Datalist.Row>
              )}
            />
          )}
        </Card.Content>
      </Dialog>
    </>
  )
}

export { AddNewGroupMembers }
